"use client";

import React, { useEffect, useState, useCallback } from "react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";
import {
  JsonRpcProvider,
  Contract,
  formatUnits,
  BrowserProvider,
} from "ethers";

const ZERO = "0x0000000000000000000000000000000000000000";
const RPC_URL = "https://bsc-dataseed.binance.org";

const CARD_PRICE = 50;
const TAX_PERCENT = 10;
const PER_CARD_TOTAL =
  CARD_PRICE + (CARD_PRICE * TAX_PERCENT) / 100;

const BSC_CHAIN_ID = "0x38"; // BSC Mainnet

const Cards = () => {
  const [address, setAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const [kindPrice, setKindPrice] = useState(0);

  const {
    buyWithUSDT,
    buyWithBNB,
    buyWithKIND,
    withdraw,
    getUserDashboard,
    getClaimable,
    getUSDTBalance,
    getBNBBalance,
    getKINDBalance,
  } = useContract();

  const [loading, setLoading] = useState(true);
  const [buyLoading, setBuyLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(true);

  const [dashboard, setDashboard] = useState({
    totalPaidCards: 0,
    activeCards: 0,
    expiredCards: 0,
    bonusCards: 0,
    totalDeposits: "0",
    rank: 0,
    currentRoiBps: 0,
    nextRank: 0,
    refsRemaining: 0,
    energyWallet: "0",
    referralWallet: "0",
    totalWithdrawnUsd: "0",
    nextWithdrawTime: 0,
    qualifiedRefs: 0,
  });

  const [claimable, setClaimable] = useState({
    energy: "0",
    referral: "0",
  });

  const [count, setCount] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("USDT");

  const [prices, setPrices] = useState({
    bnb: 0,
    kind: 0,
  });

  const [referrer, setReferrer] = useState(ZERO);

  const [balances, setBalances] = useState({
    usdt: "0",
    bnb: "0",
    kind: "0",
  });

  /*
   * -------------------------------------------------------
   * WALLET CONNECTION
   * -------------------------------------------------------
   */

  const getEthereum = () => {
    if (typeof window === "undefined") return null;

    if (!window.ethereum) {
      toast.error(
        "No wallet detected. Please install MetaMask, Bitget Wallet, Trust Wallet, or another EVM wallet."
      );
      return null;
    }

    return window.ethereum;
  };

  const connectWallet = async () => {
    try {
      const ethereum = getEthereum();

      if (!ethereum) return;

      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      if (!accounts || accounts.length === 0) {
        toast.error("No wallet account selected");
        return;
      }

      const walletAddress = accounts[0];

      // Check network
      const chainId = await ethereum.request({
        method: "eth_chainId",
      });

      if (chainId !== BSC_CHAIN_ID) {
        try {
          await ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: BSC_CHAIN_ID }],
          });
        } catch (switchError) {
          // BSC doesn't exist in wallet
          if (switchError?.code === 4902) {
            try {
              await ethereum.request({
                method: "wallet_addEthereumChain",
                params: [
                  {
                    chainId: BSC_CHAIN_ID,
                    chainName: "BNB Smart Chain",
                    nativeCurrency: {
                      name: "BNB",
                      symbol: "BNB",
                      decimals: 18,
                    },
                    rpcUrls: [
                      "https://bsc-dataseed.binance.org",
                    ],
                    blockExplorerUrls: [
                      "https://bscscan.com",
                    ],
                  },
                ],
              });
            } catch (addError) {
              console.error("Add BSC error:", addError);
              toast.error("Please switch your wallet to BSC Mainnet");
              return;
            }
          } else {
            console.error("Network switch error:", switchError);
            toast.error("Please switch your wallet to BSC Mainnet");
            return;
          }
        }
      }

      setAddress(walletAddress);
      setIsConnected(true);

      toast.success("Wallet connected");
    } catch (error) {
      console.error("Wallet connection error:", error);

      if (error?.code === 4001) {
        toast.error("Wallet connection rejected");
      } else {
        toast.error("Failed to connect wallet");
      }
    }
  };

  const disconnectWallet = () => {
    setAddress(null);
    setIsConnected(false);

    setDashboard({
      totalPaidCards: 0,
      activeCards: 0,
      expiredCards: 0,
      bonusCards: 0,
      totalDeposits: "0",
      rank: 0,
      currentRoiBps: 0,
      nextRank: 0,
      refsRemaining: 0,
      energyWallet: "0",
      referralWallet: "0",
      totalWithdrawnUsd: "0",
      nextWithdrawTime: 0,
      qualifiedRefs: 0,
    });

    setClaimable({
      energy: "0",
      referral: "0",
    });

    setBalances({
      usdt: "0",
      bnb: "0",
      kind: "0",
    });

    toast.success("Wallet disconnected");
  };

  /*
   * -------------------------------------------------------
   * CHECK EXISTING WALLET CONNECTION
   * -------------------------------------------------------
   */

  const checkWalletConnection = useCallback(async () => {
    if (typeof window === "undefined" || !window.ethereum) {
      setAddress(null);
      setIsConnected(false);
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Wallet check error:", error);
    }
  }, []);

  /*
   * -------------------------------------------------------
   * WALLET EVENTS
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (typeof window === "undefined" || !window.ethereum) {
      return;
    }

    checkWalletConnection();

    const handleAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setAddress(null);
        setIsConnected(false);
        return;
      }

      setAddress(accounts[0]);
      setIsConnected(true);
    };

    const handleChainChanged = () => {
      // Refresh account/network state after network change
      checkWalletConnection();
    };

    window.ethereum.on(
      "accountsChanged",
      handleAccountsChanged
    );

    window.ethereum.on(
      "chainChanged",
      handleChainChanged
    );

    return () => {
      if (window.ethereum.removeListener) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        window.ethereum.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, [checkWalletConnection]);

  /*
   * -------------------------------------------------------
   * TOTALS
   * -------------------------------------------------------
   */

  const totalUsd = count * PER_CARD_TOTAL;

  const totalBnb =
    prices.bnb > 0
      ? (totalUsd / prices.bnb).toFixed(4)
      : "0.0000";

  const totalKind =
    kindPrice > 0
      ? (totalUsd / kindPrice).toFixed(4)
      : "Loading...";

  /*
   * -------------------------------------------------------
   * BNB PRICE
   * -------------------------------------------------------
   */

  const fetchPrices = useCallback(async () => {
    try {
      setPricesLoading(true);

      const response = await fetch(
        "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
      );

      const data = await response.json();

      const bnbPrice = parseFloat(data.price);

      setPrices({
        bnb: bnbPrice,
        kind: 0,
      });
    } catch (error) {
      console.error(
        "Failed to fetch BNB price:",
        error
      );
    } finally {
      setPricesLoading(false);
    }
  }, []);

  /*
   * -------------------------------------------------------
   * KIND PRICE
   * -------------------------------------------------------
   */

  const provider = new JsonRpcProvider(RPC_URL);

  const KIND_ADDRESS =
    "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";

  const KIND_BNB_PAIR =
    "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  const fetchKindPrice = useCallback(async () => {
    try {
      const pair = new Contract(
        KIND_BNB_PAIR,
        [
          "function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
          "function token0() view returns (address)",
          "function token1() view returns (address)",
        ],
        provider
      );

      const [reserves, token0, token1] =
        await Promise.all([
          pair.getReserves(),
          pair.token0(),
          pair.token1(),
        ]);

      let kindReserve;
      let bnbReserve;

      if (
        token0.toLowerCase() ===
        KIND_ADDRESS.toLowerCase()
      ) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindAmount = parseFloat(
        formatUnits(kindReserve, 18)
      );

      const bnbAmount = parseFloat(
        formatUnits(bnbReserve, 18)
      );

      if (kindAmount > 0 && bnbAmount > 0) {
        const kindPriceInBNB =
          bnbAmount / kindAmount;

        const bnbUsdResponse = await fetch(
          "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
        );

        const bnbUsdData =
          await bnbUsdResponse.json();

        const bnbUsdPrice =
          parseFloat(bnbUsdData.price);

        const kindUsdPrice =
          kindPriceInBNB * bnbUsdPrice;

        setKindPrice(kindUsdPrice);

        console.log(
          `KIND: ${kindAmount.toFixed(
            0
          )} | BNB: ${bnbAmount.toFixed(
            0
          )} | KIND/USD: $${kindUsdPrice.toFixed(6)}`
        );
      }
    } catch (err) {
      console.error("Price fetch error:", err);
    }
  }, []);

  /*
   * -------------------------------------------------------
   * ROI
   * -------------------------------------------------------
   */

  const getDailyRoi = useCallback(() => {
    if (
      !dashboard.currentRoiBps ||
      dashboard.currentRoiBps === 0
    ) {
      return "0.00";
    }

    return (
      dashboard.currentRoiBps / 100
    ).toFixed(2);
  }, [dashboard.currentRoiBps]);

  const getDailyEarnings = useCallback(() => {
    if (
      !dashboard.activeCards ||
      dashboard.currentRoiBps === 0
    ) {
      return "0.00";
    }

    const daily =
      50 *
      dashboard.activeCards *
      (dashboard.currentRoiBps / 10000);

    return daily.toFixed(2);
  }, [
    dashboard.activeCards,
    dashboard.currentRoiBps,
  ]);

  const getMonthlyEarnings = useCallback(() => {
    return (
      parseFloat(getDailyEarnings()) * 30
    ).toFixed(2);
  }, [getDailyEarnings]);

  const getRankName = useCallback(() => {
    const ranks = [
      "Bronze",
      "Silver",
      "Gold",
      "Platinum",
      "Diamond",
    ];

    return ranks[dashboard.rank] || "New";
  }, [dashboard.rank]);

  /*
   * -------------------------------------------------------
   * LOAD USER DATA
   * -------------------------------------------------------
   */

  const loadData = useCallback(async () => {
    if (!address) return;

    try {
      const [d, c] = await Promise.all([
        getUserDashboard(address),
        getClaimable(address),
      ]);

      setDashboard({
        totalPaidCards: Number(
          d?.totalPaidCards || 0
        ),
        activeCards: Number(
          d?.activeCards || 0
        ),
        expiredCards: Number(
          d?.expiredCards || 0
        ),
        bonusCards: Number(
          d?.bonusCards || 0
        ),
        totalDeposits:
          d?.totalDeposits || "0",
        rank: Number(d?.rank || 0),
        currentRoiBps: Number(
          d?.currentRoiBps || 0
        ),
        nextRank: Number(
          d?.nextRank || 0
        ),
        refsRemaining: Number(
          d?.refsRemaining || 0
        ),
        energyWallet: parseFloat(
          d?.energyWallet || "0"
        ).toFixed(2),
        referralWallet: parseFloat(
          d?.referralWallet || "0"
        ).toFixed(2),
        totalWithdrawnUsd: parseFloat(
          d?.totalWithdrawnUsd || "0"
        ).toFixed(2),
        nextWithdrawTime: Number(
          d?.nextWithdrawTime || 0
        ),
        qualifiedRefs: Number(
          d?.qualifiedRefs || 0
        ),
      });

      setClaimable({
        energy: parseFloat(
          c?.energy || "0"
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),

        referral: parseFloat(
          c?.referral || "0"
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      });
    } catch (error) {
      console.error(
        "Load data error:",
        error
      );

      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [
    address,
    getUserDashboard,
    getClaimable,
  ]);

  /*
   * -------------------------------------------------------
   * REFERRER
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params =
        new URLSearchParams(
          window.location.search
        );

      const ref = params.get("ref");

      if (
        ref &&
        ref !== ZERO &&
        /^0x[a-fA-F0-9]{40}$/.test(ref)
      ) {
        setReferrer(ref);

        console.log(
          "Referral detected:",
          ref
        );
      }
    }
  }, []);

  /*
   * -------------------------------------------------------
   * BUY
   * -------------------------------------------------------
   */

  const handleBuy = async () => {
    if (!isConnected || !address) {
      toast.error("Please connect your wallet first");
      return;
    }

    const t = toast.loading(
      `Buying ${count} card(s)...`
    );

    try {
      setBuyLoading(true);

      if (!count || count <= 0) {
        toast.dismiss(t);
        toast.error("Enter valid amount");
        return;
      }

      const requiredUsd =
        count * PER_CARD_TOTAL;

      if (paymentMethod === "USDT") {
        if (
          parseFloat(balances.usdt) <
          requiredUsd
        ) {
          toast.dismiss(t);
          toast.error(
            "❌ Insufficient USDT balance"
          );
          return;
        }
      }

      if (paymentMethod === "BNB") {
        const requiredBnb =
          parseFloat(totalBnb);

        if (
          parseFloat(balances.bnb) <
          requiredBnb
        ) {
          toast.dismiss(t);
          toast.error(
            "❌ Insufficient BNB balance"
          );
          return;
        }
      }

      if (paymentMethod === "KIND") {
        const requiredKind =
          parseFloat(totalKind);

        if (
          parseFloat(balances.kind) <
          requiredKind
        ) {
          toast.dismiss(t);
          toast.error(
            "❌ Insufficient KIND balance"
          );
          return;
        }
      }

      let tx;

      if (paymentMethod === "USDT") {
        tx = await buyWithUSDT(
          count,
          referrer
        );
      } else if (
        paymentMethod === "BNB"
      ) {
        tx = await buyWithBNB(
          count,
          referrer
        );
      } else if (
        paymentMethod === "KIND"
      ) {
        tx = await buyWithKIND(
          count,
          referrer
        );
      }

      /*
       * If your useContract returns a transaction
       * response, wait for confirmation.
       */
      if (tx?.wait) {
        await tx.wait();
      }

      toast.dismiss(t);
      toast.success(
        "Purchase successful 🚀"
      );

      await loadData();
      await loadBalances();

      return tx;
    } catch (err) {
      toast.dismiss(t);

      const raw =
        err?.reason ||
        err?.data?.message ||
        err?.error?.message ||
        err?.shortMessage ||
        err?.message ||
        "Transaction failed";

      const clean = raw
        .replace(
          "execution reverted:",
          ""
        )
        .replace("Error:", "")
        .trim();

      toast.error(clean);

      console.error(
        "BUY ERROR:",
        err
      );
    } finally {
      setBuyLoading(false);
    }
  };

  /*
   * -------------------------------------------------------
   * WITHDRAW
   * -------------------------------------------------------
   */

  const handleWithdraw = async () => {
    const total =
      parseFloat(
        claimable.energy.replace(
          /,/g,
          ""
        )
      ) +
      parseFloat(
        claimable.referral.replace(
          /,/g,
          ""
        )
      );

    if (total <= 0) {
      return toast.error(
        "Nothing to withdraw"
      );
    }

    const t =
      toast.loading("Withdrawing...");

    try {
      setWithdrawLoading(true);

      const tx = await withdraw();

      if (tx?.wait) {
        await tx.wait();
      }

      toast.dismiss(t);
      toast.success(
        "Withdraw success"
      );

      await loadData();
      await loadBalances();
    } catch (error) {
      console.error(
        "Withdraw error:",
        error
      );

      toast.dismiss(t);
      toast.error(
        error?.shortMessage ||
          error?.reason ||
          "Withdraw failed"
      );
    } finally {
      setWithdrawLoading(false);
    }
  };

  /*
   * -------------------------------------------------------
   * BALANCES
   * -------------------------------------------------------
   */

  const loadBalances = useCallback(async () => {
    if (!address) return;

    try {
      const [
        usdt,
        bnb,
        kind,
      ] = await Promise.all([
        getUSDTBalance(),
        getBNBBalance(),
        getKINDBalance(),
      ]);

      setBalances({
        usdt: Number(usdt).toFixed(2),
        bnb: Number(bnb).toFixed(4),
        kind: Number(kind).toFixed(2),
      });
    } catch (err) {
      console.error(
        "Balance error:",
        err
      );
    }
  }, [
    address,
    getUSDTBalance,
    getBNBBalance,
    getKINDBalance,
  ]);

  /*
   * -------------------------------------------------------
   * PRICE INTERVAL
   * -------------------------------------------------------
   */

  useEffect(() => {
    fetchPrices();

    const priceInterval =
      setInterval(
        fetchPrices,
        60000
      );

    return () =>
      clearInterval(priceInterval);
  }, [fetchPrices]);

  /*
   * -------------------------------------------------------
   * USER DATA INTERVAL
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!isConnected || !address) {
      setLoading(false);
      return;
    }

    loadData();
  }, [
    isConnected,
    address,
    loadData,
  ]);

  useEffect(() => {
    if (!isConnected || !address)
      return;

    const interval =
      setInterval(
        loadData,
        30000
      );

    return () =>
      clearInterval(interval);
  }, [
    loadData,
    isConnected,
    address,
  ]);

  /*
   * -------------------------------------------------------
   * BALANCE INTERVAL
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!isConnected || !address)
      return;

    loadBalances();

    const interval =
      setInterval(
        loadBalances,
        95000
      );

    return () =>
      clearInterval(interval);
  }, [
    isConnected,
    address,
    loadBalances,
  ]);

  /*
   * -------------------------------------------------------
   * KIND PRICE INTERVAL
   * -------------------------------------------------------
   */

  useEffect(() => {
    fetchKindPrice();

    const interval =
      setInterval(
        fetchKindPrice,
        15000
      );

    return () =>
      clearInterval(interval);
  }, [fetchKindPrice]);

  /*
   * -------------------------------------------------------
   * DISCONNECTED UI
   * -------------------------------------------------------
   */

  if (!isConnected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#111111] px-4">
        <div className="w-full max-w-sm sm:max-w-md bg-[#171717] border border-[#2A2A2A] rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center shadow-2xl">

          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 bg-[#D3AF37]/10 border-2 border-[#D3AF37]/30 rounded-2xl flex items-center justify-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-[#D3AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#D3AF37] mb-4 leading-tight">
            Connect Wallet
          </h2>

          <p className="text-[#B8B8B8] mb-8 text-base sm:text-lg">
            Connect to manage your energy cards
          </p>

          <button
            onClick={connectWallet}
            className="w-full bg-[#D3AF37] hover:bg-[#B59410] py-3 sm:py-4 px-6 sm:px-8 rounded-2xl font-bold text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 text-white"
          >
            🔗 Connect Wallet
          </button>

        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * MAIN UI
   * -------------------------------------------------------
   */

  return (
    <div className="min-h-screen p-4 sm:p-6 text-white">

      {/* HEADER */}
      <div className="bg-[#171717] border border-[#2A2A2A] p-6 sm:p-8 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 shadow-2xl">

        <div className="flex flex-col sm:flex-row justify-between gap-5">

          <h1 className="text-2xl sm:text-3xl md:text-5xl font-black text-[#D3AF37] mb-2 leading-tight">
            Energy Cards Dashboard
          </h1>

          <button
            onClick={disconnectWallet}
            className="self-start px-5 py-3 rounded-xl bg-[#2A2A2A] hover:bg-red-600 text-white font-semibold transition"
          >
            Disconnect
          </button>

        </div>

        {/* WALLET BALANCES */}
        <div className="bg-[#111111] border border-[#2A2A2A] p-4 sm:p-6 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8">

          <div className="text-xs sm:text-sm text-[#D3AF37] mb-3">
            Connected Wallet
          </div>

          <div className="text-xs sm:text-sm text-gray-400 font-mono mb-5 break-all">
            {address}
          </div>

          <div className="text-xs sm:text-sm text-[#D3AF37] mb-3">
            Wallet Balances
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">

            <div>
              <div className="text-lg sm:text-xl font-bold text-white">
                ${balances.usdt}
              </div>
              <div className="text-xs text-[#B8B8B8]">
                USDT
              </div>
            </div>

            <div>
              <div className="text-lg sm:text-xl font-bold text-white">
                {balances.bnb}
              </div>
              <div className="text-xs text-[#B8B8B8]">
                BNB
              </div>
            </div>

            <div>
              <div className="text-lg sm:text-xl font-bold text-white">
                {balances.kind}
              </div>
              <div className="text-xs text-[#B8B8B8]">
                KIND
              </div>
            </div>

          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 p-4 sm:p-6 bg-[#111111] rounded-2xl sm:rounded-3xl border border-[#2A2A2A]">

          <div className="text-center">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#D3AF37]">
              {getDailyRoi()}% / day
            </div>
            <div className="text-[#B8B8B8] text-xs sm:text-sm">
              {getRankName()}
            </div>
          </div>

          <div className="text-center">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
              ${getDailyEarnings()}
            </div>
            <div className="text-xs sm:text-sm text-[#B8B8B8]">
              Daily
            </div>
          </div>

          <div className="text-center">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
              ${getMonthlyEarnings()}
            </div>
            <div className="text-xs sm:text-sm text-[#B8B8B8]">
              Monthly
            </div>
          </div>

          <div className="text-center">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#D3AF37]">
              {dashboard.refsRemaining}
            </div>
            <div className="text-xs sm:text-sm text-[#B8B8B8]">
              Refs to Next Rank
            </div>
          </div>

        </div>
      </div>

      {/* PAYMENT OPTIONS */}
      <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 flex-wrap">

        {["USDT", "BNB", "KIND"].map((m) => (
          <button
            key={m}
            onClick={() =>
              setPaymentMethod(m)
            }
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-bold transition-all duration-300 text-white text-sm sm:text-base ${
              paymentMethod === m
                ? "bg-[#D3AF37] shadow-xl shadow-[#D3AF37]/20"
                : "bg-[#171717] hover:bg-[#1F1F1F] border border-[#2A2A2A] hover:border-[#D3AF37]/40"
            }`}
          >
            {m}
          </button>
        ))}

      </div>

      {/* LIVE PRICES */}
      {(prices.bnb > 0 ||
        kindPrice > 0) && (
        <div className="bg-[#171717] border border-[#2A2A2A] p-4 sm:p-6 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8">

          <div className="text-xs sm:text-sm text-[#D3AF37] mb-2">
            Live Prices
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm sm:text-base">

            {prices.bnb > 0 && (
              <div className="text-center">
                <div className="font-bold text-white">
                  BNB
                </div>

                <div className="text-[#D3AF37]">
                  $
                  {prices.bnb.toLocaleString()}
                </div>
              </div>
            )}

            {kindPrice > 0 && (
              <div className="text-center">
                <div className="font-bold text-white">
                  KIND
                </div>

                <div className="text-[#D3AF37] text-sm font-mono px-3 py-1 rounded-lg">
                  ${kindPrice.toFixed(6)}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* BUY SECTION */}
      <div className="bg-[#171717] border border-[#2A2A2A] p-6 sm:p-8 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8">

        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#D3AF37] leading-tight">
          🛒 Buy Cards ($
          {totalUsd.toLocaleString()})
        </h2>

        <div className="flex flex-col gap-4 mb-6">

          <input
            type="number"
            min="0"
            placeholder="Enter amount"
            value={count}
            onChange={(e) =>
              setCount(
                e.target.value === ""
                  ? ""
                  : Number(e.target.value)
              )
            }
            className="w-full p-4 sm:p-5 rounded-2xl bg-[#111111] border border-[#2A2A2A] text-xl sm:text-2xl font-mono text-white focus:border-[#D3AF37] focus:outline-none focus:ring-2 focus:ring-[#D3AF37]/30"
          />

          <div className="bg-[#111111] border border-[#2A2A2A] rounded-2xl p-4 sm:p-5 text-center sm:text-left">

            <div className="text-lg sm:text-xl font-mono text-[#B8B8B8]">

              {paymentMethod ===
                "USDT" && (
                <div>
                  💰 Total:{" "}
                  <span className="text-white">
                    $
                    {totalUsd.toLocaleString()}{" "}
                    USDT
                  </span>
                </div>
              )}

              {paymentMethod ===
                "BNB" && (
                <div>
                  💰 Total:{" "}
                  <span className="text-white">
                    {totalBnb} BNB
                  </span>

                  <div className="text-xs text-[#B8B8B8] mt-1">
                    ($
                    {totalUsd.toLocaleString()})
                  </div>
                </div>
              )}

              {paymentMethod ===
                "KIND" && (
                <div>
                  💰 Total:{" "}
                  <span className="text-white">
                    {totalKind} KIND
                  </span>

                  <div className="text-xs text-[#B8B8B8] mt-1">
                    ($
                    {totalUsd.toLocaleString()})
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        <button
          onClick={handleBuy}
          disabled={buyLoading}
          className="w-full bg-[#D3AF37] hover:bg-[#B59410] py-4 sm:py-5 px-8 rounded-2xl font-bold text-lg sm:text-xl shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-[#D3AF37]/30 text-white"
        >
          {buyLoading
            ? "⏳ Processing..."
            : `🛒 Buy ${count} Card(s) with ${paymentMethod}`}
        </button>

      </div>

    </div>
  );
};

export default Cards;